<template>
  <div>
    <div class="container-fluid">
      <div class="row">
        <div class="col-12" style="margin-top: 20px;">
          <div class="card download-center-card">
            <div class="card-header d-flex align-items-center justify-content-between flex-wrap" style="gap: 8px;">
              <div>
                <h3 class="mb-0">Centro de Descargas</h3>
                <small class="text-muted">Historial de solicitudes automáticas y manuales</small>
              </div>
              <button
                v-if="hasRfcSelected && canRequestDownloads"
                type="button"
                class="btn btn-primary btn-sm"
                :disabled="manualDownloadModal.loading || Number(manualLimits.remaining_downloads_today || 0) <= 0 || Number(manualLimits.remaining_cfdis_today || 0) <= 0"
                @click="openManualDownloadModal"
              >
                Descarga Manual
              </button>
            </div>
            <div class="card-body">
              <div v-if="efirmaIncident?.warning_message" class="alert alert-warning py-2" role="alert">
                {{ efirmaIncident.warning_message }}
              </div>

              <div v-if="hasRfcSelected" class="download-center-limits mb-3">
                <span>Descargas manuales hoy: <strong>{{ manualLimits.used_downloads_today || 0 }}/{{ manualLimits.max_downloads_per_day || 3 }}</strong></span>
                <span>CFDIs nuevos hoy (manual + inicial + daily): <strong>{{ formatInteger(manualLimits.used_cfdis_today || 0) }}/{{ formatInteger(manualLimits.max_cfdis_per_day || 2000) }}</strong></span>
              </div>

              <div v-if="loading" class="text-center py-3">Cargando historial de descargas...</div>
              <div v-else-if="error" class="alert alert-warning py-2" role="alert">{{ error }}</div>
              <div v-else>
                <div class="download-group download-group-manual">
                  <div class="download-group-header">
                    <h6 class="mb-0">Descargas manuales del día</h6>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm download-center-table">
                      <thead>
                        <tr>
                          <th>Fecha y Hora Solicitud</th>
                          <th>Fecha Inicial</th>
                          <th>Fecha Final</th>
                          <th>Estado</th>
                          <th class="text-end">CFDIs Recibidos</th>
                          <th class="text-end">CFDIs Emitidos</th>
                          <th class="text-end">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!groups.manual_today.length">
                          <td colspan="7" class="text-center">Sin descargas manuales hoy.</td>
                        </tr>
                        <tr v-for="item in groups.manual_today" :key="item.group_id">
                          <td>{{ item.requested_at || '-' }}</td>
                          <td>{{ item.range_start || '-' }}</td>
                          <td>{{ item.range_end || '-' }}</td>
                          <td>
                            <span class="status-badge" :class="`status-${String(item.status || '').toLowerCase()}`">{{ formatStatusLabel(item.status) }}</span>
                          </td>
                          <td class="text-end">{{ formatInteger(item.received_new_cfdis || 0) }}</td>
                          <td class="text-end">{{ formatInteger(item.issued_new_cfdis || 0) }}</td>
                          <td class="text-end">
                            <button
                              v-if="canCancelManualDownload(item)"
                              type="button"
                              class="btn btn-xs btn-outline-danger"
                              :disabled="isCancelingManualDownload(item.group_id)"
                              @click="cancelManualDownload(item)"
                            >
                              {{ isCancelingManualDownload(item.group_id)
                                ? (manualDownloadActionLabel(item) === 'Borrar' ? 'Borrando...' : 'Cancelando...')
                                : manualDownloadActionLabel(item)
                              }}
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div class="download-group mt-3">
                  <div class="download-group-header">
                    <h6 class="mb-0">Descargas diarias (últimos 10 días)</h6>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm download-center-table">
                      <thead>
                        <tr>
                          <th>Día de solicitud</th>
                          <th>Fecha Inicial</th>
                          <th>Fecha Final</th>
                          <th>Estado</th>
                          <th class="text-end">CFDIs Recibidos</th>
                          <th class="text-end">CFDIs Emitidos</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!groups.daily_last_10_days.length">
                          <td colspan="6" class="text-center">Sin descargas diarias en los últimos 10 días.</td>
                        </tr>
                        <tr v-for="item in groups.daily_last_10_days" :key="item.group_id">
                          <td>{{ item.requested_at || '-' }}</td>
                          <td>{{ item.range_start || '-' }}</td>
                          <td>{{ item.range_end || '-' }}</td>
                          <td>
                            <span class="status-badge" :class="`status-${String(item.status || '').toLowerCase()}`">{{ formatStatusLabel(item.status) }}</span>
                          </td>
                          <td class="text-end">{{ formatInteger(item.received_new_cfdis || 0) }}</td>
                          <td class="text-end">{{ formatInteger(item.issued_new_cfdis || 0) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div class="download-group mt-3">
                  <div class="download-group-header">
                    <h6 class="mb-0">Descargas mensuales (últimos 12 meses)</h6>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm download-center-table">
                      <thead>
                        <tr>
                          <th>Periodo</th>
                          <th>Fecha Inicial</th>
                          <th>Fecha Final</th>
                          <th>Estado</th>
                          <th class="text-end">CFDIs Recibidos</th>
                          <th class="text-end">CFDIs Emitidos</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!groups.monthly_last_12_months.length">
                          <td colspan="6" class="text-center">Sin descargas mensuales registradas en los últimos 12 meses.</td>
                        </tr>
                        <tr v-for="item in groups.monthly_last_12_months" :key="item.group_id">
                          <td>{{ item.requested_at || '-' }}</td>
                          <td>{{ item.range_start || '-' }}</td>
                          <td>{{ item.range_end || '-' }}</td>
                          <td>
                            <span class="status-badge" :class="`status-${String(item.status || '').toLowerCase()}`">{{ formatStatusLabel(item.status) }}</span>
                          </td>
                          <td class="text-end">{{ formatInteger(item.received_new_cfdis || 0) }}</td>
                          <td class="text-end">{{ formatInteger(item.issued_new_cfdis || 0) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div class="download-group mt-3">
                  <div class="download-group-header">
                    <h6 class="mb-0">Descarga inicial</h6>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm download-center-table">
                      <thead>
                        <tr>
                          <th>Día de solicitud</th>
                          <th>Fecha Inicial</th>
                          <th>Fecha Final</th>
                          <th>Estado</th>
                          <th class="text-end">CFDIs Recibidos</th>
                          <th class="text-end">CFDIs Emitidos</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!groups.initial_summary">
                          <td colspan="6" class="text-center">Sin descarga inicial registrada.</td>
                        </tr>
                        <tr v-else>
                          <td>{{ groups.initial_summary.requested_at || '-' }}</td>
                          <td>{{ groups.initial_summary.range_start || '-' }}</td>
                          <td>{{ groups.initial_summary.range_end || '-' }}</td>
                          <td>
                            <span class="status-badge" :class="`status-${String(groups.initial_summary.status || '').toLowerCase()}`">{{ formatStatusLabel(groups.initial_summary.status) }}</span>
                          </td>
                          <td class="text-end">{{ formatInteger(groups.initial_summary.received_new_cfdis || 0) }}</td>
                          <td class="text-end">{{ formatInteger(groups.initial_summary.issued_new_cfdis || 0) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div class="download-group mt-3">
                  <div class="download-group-header">
                    <h6 class="mb-0">Descargas de CFDIs (XML + PDF)</h6>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm download-center-table">
                      <thead>
                        <tr>
                          <th>Fecha y hora</th>
                          <th>Tipo</th>
                          <th>Periodo</th>
                          <th>Estado</th>
                          <th class="text-end">CFDIs</th>
                          <th>Disponible hasta</th>
                          <th class="text-end">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!cfdiFileDownloads.length">
                          <td colspan="9" class="text-center">Sin solicitudes de descarga de CFDIs.</td>
                        </tr>
                        <tr v-for="item in cfdiFileDownloads" :key="item.id">
                          <td>{{ item.requested_at || '-' }}</td>
                          <td>{{ formatNatureLabel(item.nature) }}</td>
                          <td>{{ item.period || '-' }}</td>
                          <td>
                            <span class="status-badge" :class="`status-${String(item.status || '').toLowerCase()}`">{{ formatStatusLabel(item.status) }}</span>
                          </td>
                          <td class="text-end">{{ formatInteger(item.total_cfdis || 0) }}</td>
                          <td>{{ item.expires_at || '-' }}</td>
                          <td class="text-end">
                            <button
                              type="button"
                              class="btn btn-xs btn-primary"
                              :disabled="!item.can_download || isDownloadingBundle(item.id)"
                              @click="downloadCfdiFilesBundle(item)"
                            >
                              {{ isDownloadingBundle(item.id) ? 'Descargando...' : 'Descargar' }}
                            </button>
                            <button
                              type="button"
                              class="btn btn-xs btn-outline-danger ms-1"
                              :disabled="!item.can_delete || isDeletingBundle(item.id)"
                              @click="deleteCfdiFilesBundle(item)"
                            >
                              {{ isDeletingBundle(item.id) ? 'Borrando...' : 'Borrar' }}
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="pendingRequestModal.visible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" @click.self="closePendingRequestModal">
      <div class="modal-dialog modal-dialog-centered pending-request-modal-dialog" role="document">
        <div class="modal-content pending-request-modal-content">
          <div class="modal-header pending-request-modal-header">
            <h5 class="modal-title"><i class="fa fa-download text-warning me-2"></i>Descargas en curso</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closePendingRequestModal"></button>
          </div>
          <div class="modal-body pending-request-modal-body">
            <p class="pending-request-title mb-1">{{ pendingRequestModal.title }}</p>
            <p v-if="pendingRequestModal.subtitle" class="pending-request-subtitle mb-3">{{ pendingRequestModal.subtitle }}</p>

            <div class="pending-request-info-row">
              <div class="pending-request-info-item">
                <span class="pending-request-info-label">RFC</span>
                <span class="pending-request-info-value">{{ normalizedRfc || '-' }}</span>
              </div>
              <div class="pending-request-info-item">
                <span class="pending-request-info-label">Estado</span>
                <span class="pending-request-info-value">{{ pendingRequestModal.statusLabel || '-' }}</span>
              </div>
              <div class="pending-request-info-item">
                <span class="pending-request-info-label">Tipo</span>
                <span class="pending-request-info-value">{{ pendingRequestModal.typeLabel || '-' }}</span>
              </div>
              <div class="pending-request-info-item">
                <span class="pending-request-info-label">Periodo</span>
                <span class="pending-request-info-value">{{ pendingRequestModal.periodLabel || '-' }}</span>
              </div>
            </div>

            <div class="pending-request-warning">
              <i class="fa fa-info-circle"></i>
              <div>
                <strong>{{ pendingRequestModal.statusLabel || 'Descarga en proceso' }}</strong>
                <p>{{ pendingRequestModal.scheduleLabel || 'Mantén esta ventana abierta para seguir el avance en la tabla del Centro de Descargas.' }}</p>
              </div>
            </div>
          </div>
          <div class="modal-footer pending-request-modal-footer">
            <button type="button" class="btn btn-primary" @click="closePendingRequestModal">Entendido</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="pendingRequestModal.visible" class="modal-backdrop fade show"></div>

    <div v-if="manualDownloadModal.visible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" @click.self="closeManualDownloadModal">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Descarga manual</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeManualDownloadModal"></button>
          </div>
          <div class="modal-body">
            <p>
              Esta opción realiza una petición al SAT para descargar los CFDIs faltantes,
              incluyendo los emitidos y recibidos del periodo seleccionado.
            </p>
            <p>
              Solo se permiten <strong>3 descargas manuales por día</strong> y un máximo de
              <strong>2,000 CFDIs nuevos por día (manual + inicial + daily)</strong>.
            </p>

            <div class="manual-download-period">
              <YearMonthSelect style="float: right;" :period="manualDownloadModal.period" :value="manualDownloadModal.period" @change="changeManualDownloadPeriod" />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" @click="closeManualDownloadModal">Cancelar</button>
            <button type="button" class="btn btn-primary" :disabled="manualDownloadModal.loading || !manualDownloadModal.period" @click="submitManualDownload">
              {{ manualDownloadModal.loading ? 'Programando...' : 'Continuar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="manualDownloadModal.visible" class="modal-backdrop fade show"></div>

    <div v-if="confirmActionModal.visible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" @click.self="closeConfirmActionModal">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ confirmActionModal.title }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeConfirmActionModal"></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">{{ confirmActionModal.message }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" @click="closeConfirmActionModal">Cancelar</button>
            <button type="button" class="btn btn-danger" :disabled="confirmActionModal.loading" @click="runConfirmAction">
              {{ confirmActionModal.loading ? confirmActionModal.loadingLabel : confirmActionModal.confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="confirmActionModal.visible" class="modal-backdrop fade show"></div>

    <div v-if="alertModal.visible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" @click.self="closeAlertModal">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ alertModal.title }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeAlertModal"></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">{{ alertModal.message }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click="closeAlertModal">Entendido</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="alertModal.visible" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import ws from '@/services/dashboard';
import accountService from '@/services/account';
import YearMonthSelect from '@/components/cfdis/YearMonthSelect.vue';
import { getGlobalPeriod } from '@/services/globalPeriod';

export default {
  name: 'DownloadCenter',
  components: {
    YearMonthSelect,
  },
  data() {
    return {
      rfc: '',
      loading: false,
      error: '',
      groups: {
        manual_today: [],
        daily_last_10_days: [],
        monthly_last_12_months: [],
        initial_summary: null,
      },
      cfdiFileDownloads: [],
      manualLimits: {
        used_downloads_today: 0,
        max_downloads_per_day: 3,
        remaining_downloads_today: 3,
        used_cfdis_today: 0,
        max_cfdis_per_day: 2000,
        remaining_cfdis_today: 2000,
      },
      manualDownloadModal: {
        visible: false,
        loading: false,
        period: '',
      },
      confirmActionModal: {
        visible: false,
        loading: false,
        title: '',
        message: '',
        confirmLabel: '',
        loadingLabel: '',
        action: null,
      },
      alertModal: {
        visible: false,
        title: '',
        message: '',
      },
      pendingRequestModal: {
        visible: false,
        title: '',
        subtitle: '',
        statusLabel: '',
        typeLabel: '',
        periodLabel: '',
        scheduleLabel: '',
      },
      efirmaIncident: null,
      bundleActionState: {
        downloading: {},
        deleting: {},
        cancelingManual: {},
      },
      subscriptionStatus: null,
    };
  },
  computed: {
    normalizedRfc() {
      return String(this.rfc || '').trim().toUpperCase();
    },
    hasRfcSelected() {
      return this.normalizedRfc !== '';
    },
    canRequestDownloads() {
      return Boolean(this.subscriptionStatus?.can_request_downloads) && !this.efirmaIncident;
    },
  },
  mounted() {
    this.rfc = localStorage.getItem('rfc') || '';
    this.bootstrap();
  },
  methods: {
    ensureRfcSelected(showMessage = true) {
      if (this.hasRfcSelected) {
        return true;
      }

      if (showMessage) {
        this.error = 'Debes registrar y seleccionar un RFC para usar el Centro de Descargas.';
      }

      this.loading = false;
      return false;
    },
    async bootstrap() {
      if (!this.ensureRfcSelected()) {
        return;
      }

      await Promise.all([
        this.fetchHistory({ showPendingModal: true }),
        this.fetchSubscriptionStatus(),
      ]);
    },
    async fetchSubscriptionStatus() {
      if (!this.ensureRfcSelected(false)) {
        this.subscriptionStatus = null;
        return;
      }

      try {
        const response = await accountService.getSubscription();
        this.subscriptionStatus = response?.data?.status || null;
      } catch (error) {
        this.subscriptionStatus = null;
      }
    },
    formatInteger(value) {
      return Number(value || 0).toLocaleString('es-MX');
    },
    formatStatusLabel(status) {
      const normalized = String(status || '').trim().toLowerCase();

      if (!normalized) {
        return '-';
      }

      const labels = {
        requested: 'Solicitado',
        pending: 'Pendiente',
        partial: 'Parcial',
        completed: 'Confirmado',
        in_progress: 'En proceso',
        processing: 'En proceso',
        downloading: 'Descargando...',
        available: 'Disponible',
        downloaded: 'Descargado',
        failed: 'Fallido',
      };

      return labels[normalized] || normalized;
    },
    formatNatureLabel(nature) {
      const normalized = String(nature || '').trim().toLowerCase();

      if (normalized === 'issued') {
        return 'Emitidos';
      }

      if (normalized === 'received') {
        return 'Recibidos';
      }

      return '-';
    },
    formatPeriodLabel(period) {
      const normalized = String(period || '').trim();
      if (!normalized) {
        return 'Periodo no definido';
      }

      const monthMatch = normalized.match(/^(\d{4})-(\d{2})$/);
      if (monthMatch) {
        const year = Number(monthMatch[1]);
        const month = Number(monthMatch[2]);
        if (month >= 1 && month <= 12) {
          const formatted = new Intl.DateTimeFormat('es-MX', {
            month: 'long',
            year: 'numeric',
          }).format(new Date(year, month - 1, 1));

          return formatted.charAt(0).toUpperCase() + formatted.slice(1);
        }
      }

      const yearMatch = normalized.match(/^(\d{4})$/);
      if (yearMatch) {
        return `Año ${yearMatch[1]}`;
      }

      return normalized;
    },
    formatDateTimeLabel(value) {
      const parsedDate = this.parseDateTimeValue(value);
      if (!parsedDate) {
        return String(value || '').trim();
      }

      return parsedDate.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    },
    parseDateTimeValue(value) {
      const raw = String(value || '').trim();
      if (!raw) {
        return null;
      }

      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
      const parsedDate = new Date(normalized);

      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      return parsedDate;
    },
    buildPendingRequestTitle(item) {
      const status = String(item?.status || '').trim().toLowerCase();
      const normalizedStatus = status === 'downloading' ? 'in_progress' : status;
      const explicitNatureLabel = String(item?.nature_label || '').trim();
      const type = String(item?.type || '').trim().toLowerCase();
      const natureLabel = explicitNatureLabel || (type === 'issued' ? 'Emitidos' : 'Recibidos');
      const explicitPeriodLabel = String(item?.period_label || '').trim();
      const periodLabel = explicitPeriodLabel || this.formatPeriodLabel(item?.period || '');
      const effectiveDateRaw = item?.scheduled_date || item?.request_date || '';
      const effectiveDate = this.parseDateTimeValue(effectiveDateRaw);
      const dateLabel = this.formatDateTimeLabel(effectiveDateRaw);

      if (normalizedStatus === 'in_progress') {
        return {
          title: 'Descargando CFDIs',
          subtitle: `CFDIs ${natureLabel} - ${periodLabel}`,
          statusLabel: 'En proceso',
          typeLabel: natureLabel,
          periodLabel,
          scheduleLabel: dateLabel ? `Inicio estimado: ${dateLabel}` : 'La descarga está actualmente en ejecución.',
        };
      }

      // Determine schedule label when the request is pending or waiting
      let scheduleLabel = 'La solicitud está pendiente y se ejecutará en cuanto haya disponibilidad.';

      if (effectiveDate) {
        const now = Date.now();
        if (effectiveDate.getTime() > now) {
          // Future scheduled date
          scheduleLabel = dateLabel ? `Programada para: ${dateLabel}` : scheduleLabel;
        } else {
          // Scheduled date has passed
          const diffMs = now - effectiveDate.getTime();
          const thirtyMinutesMs = 30 * 60 * 1000;
          if (diffMs <= thirtyMinutesMs) {
            // Passed within the last 30 minutes
            scheduleLabel = 'La descarga está próxima y debería comenzar en los próximos minutos.';
          } else {
            // Passed more than 30 minutes ago
            scheduleLabel = 'La solicitud sigue esperando. Hay muchas peticiones de descarga o existe intermitencia en el SAT.';
          }
        }
      }

      return {
        title: 'Preparando descarga de CFDIs',
        subtitle: `CFDIs ${natureLabel} - ${periodLabel}`,
        statusLabel: 'Esperando',
        typeLabel: natureLabel,
        periodLabel,
        scheduleLabel,
      };
    },
    parseDateFromDdMmYyyy(value) {
      const raw = String(value || '').trim();
      const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!match) {
        return null;
      }

      const day = Number(match[1]);
      const month = Number(match[2]);
      const year = Number(match[3]);

      if (!day || !month || !year) {
        return null;
      }

      return new Date(year, month - 1, day);
    },
    formatMonthYearFromDate(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return 'Periodo no definido';
      }

      const formatted = new Intl.DateTimeFormat('es-MX', {
        month: 'long',
        year: 'numeric',
      }).format(date);

      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },
    buildPendingHintFromGroups() {
      const candidateGroups = [];

      if (Array.isArray(this.groups?.manual_today)) {
        candidateGroups.push(...this.groups.manual_today);
      }

      if (Array.isArray(this.groups?.daily_last_10_days)) {
        candidateGroups.push(...this.groups.daily_last_10_days);
      }

      if (this.groups?.initial_summary) {
        candidateGroups.push(this.groups.initial_summary);
      }

      if (!candidateGroups.length) {
        return null;
      }

      const statusPriority = {
        in_progress: 0,
        downloading: 0,
        requested: 1,
      };

      const pending = candidateGroups
        .map((item) => {
          const status = String(item?.status || '').trim().toLowerCase();
          if (!(status in statusPriority)) {
            return null;
          }

          return {
            raw: item,
            status,
            priority: statusPriority[status],
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (a.priority !== b.priority) {
            return a.priority - b.priority;
          }

          const aDate = String(a.raw?.requested_at_raw || '');
          const bDate = String(b.raw?.requested_at_raw || '');
          if (aDate === bDate) {
            return 0;
          }

          return aDate < bDate ? -1 : 1;
        });

      if (!pending.length) {
        return null;
      }

      const selected = pending[0].raw || {};
      const types = Array.isArray(selected?.types) ? selected.types.map((t) => String(t || '').toLowerCase()) : [];

      let natureLabel = 'Recibidos';
      if (types.includes('issued') && types.includes('received')) {
        natureLabel = 'Recibidos y Emitidos';
      } else if (types.includes('issued')) {
        natureLabel = 'Emitidos';
      }

      const rangeEndDate = this.parseDateFromDdMmYyyy(selected?.range_end);
      const rangeStartDate = this.parseDateFromDdMmYyyy(selected?.range_start);
      const periodDate = rangeEndDate || rangeStartDate || null;
      const periodLabel = this.formatMonthYearFromDate(periodDate);

      return {
        status: pending[0].status,
        nature_label: natureLabel,
        period_label: periodLabel,
        scheduled_date: selected?.scheduled_at_raw || selected?.requested_at_raw || '',
      };
    },
    applyPendingRequestHint(item) {
      if (!item) {
        return;
      }

      const content = this.buildPendingRequestTitle(item);
      this.pendingRequestModal.title = content?.title || '';
      this.pendingRequestModal.subtitle = content?.subtitle || '';
      this.pendingRequestModal.statusLabel = content?.statusLabel || '';
      this.pendingRequestModal.typeLabel = content?.typeLabel || '';
      this.pendingRequestModal.periodLabel = content?.periodLabel || '';
      this.pendingRequestModal.scheduleLabel = content?.scheduleLabel || '';
      this.pendingRequestModal.visible = true;
    },
    closePendingRequestModal() {
      this.pendingRequestModal.visible = false;
      this.pendingRequestModal.title = '';
      this.pendingRequestModal.subtitle = '';
      this.pendingRequestModal.statusLabel = '';
      this.pendingRequestModal.typeLabel = '';
      this.pendingRequestModal.periodLabel = '';
      this.pendingRequestModal.scheduleLabel = '';
    },
    async readBlobAsText(blob) {
      if (!(blob instanceof Blob)) {
        return '';
      }

      try {
        return await blob.text();
      } catch (error) {
        return '';
      }
    },
    async extractMessageFromBlob(blob) {
      const raw = (await this.readBlobAsText(blob)).trim();
      if (!raw) {
        return '';
      }

      try {
        const payload = JSON.parse(raw);
        const message = String(payload?.message || '').trim();
        if (message) {
          return message;
        }
      } catch (error) {
        // Ignore parse errors and fallback to plain text.
      }

      return raw.length <= 300 ? raw : '';
    },
    async blobHasZipSignature(blob) {
      if (!(blob instanceof Blob) || blob.size <= 0) {
        return false;
      }

      try {
        const signatureBuffer = await blob.slice(0, 4).arrayBuffer();
        const signature = new Uint8Array(signatureBuffer);

        // ZIP signatures start with PK (0x50, 0x4B).
        return signature[0] === 0x50 && signature[1] === 0x4b;
      } catch (error) {
        return false;
      }
    },
    async normalizeDownloadBlob(responsePayload) {
      const blob = responsePayload instanceof Blob
        ? responsePayload
        : new Blob([responsePayload], { type: 'application/octet-stream' });

      if (blob.size <= 0) {
        throw new Error('El archivo ZIP está vacío o no pudo generarse correctamente.');
      }

      const blobType = String(blob.type || '').toLowerCase();
      if (blobType.includes('application/json') || blobType.startsWith('text/')) {
        const message = await this.extractMessageFromBlob(blob);
        throw new Error(message || 'No fue posible generar el archivo ZIP solicitado.');
      }

      const hasZipSignature = await this.blobHasZipSignature(blob);
      if (!hasZipSignature) {
        const message = await this.extractMessageFromBlob(blob);
        throw new Error(message || 'La respuesta no contiene un archivo ZIP válido.');
      }

      return blob;
    },
    async resolveBundleDownloadErrorMessage(error) {
      const responseBlob = error?.response?.data;
      if (responseBlob instanceof Blob) {
        const parsedMessage = await this.extractMessageFromBlob(responseBlob);
        if (parsedMessage) {
          return parsedMessage;
        }
      }

      const directMessage = String(error?.response?.data?.message || error?.message || '').trim();
      return directMessage || 'No fue posible descargar el archivo ZIP.';
    },
    isTransientBundleDownloadError(error) {
      const rawMessage = String(error?.message || '').toLowerCase();
      const responseStatus = Number(error?.response?.status || 0);

      if (responseStatus >= 500) {
        return true;
      }

      return rawMessage.includes('network error')
        || rawMessage.includes('failed to fetch')
        || rawMessage.includes('err_network')
        || rawMessage.includes('timeout');
    },
    async requestBundleDownloadWithRetry(requestId, retries = 1) {
      let lastError = null;

      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          const response = await ws.downloadCfdiFilesBundle(this.rfc, requestId, { skipLoading: true });
          return await this.normalizeDownloadBlob(response);
        } catch (error) {
          lastError = error;

          if (attempt < retries && this.isTransientBundleDownloadError(error)) {
            await new Promise((resolve) => setTimeout(resolve, 700));
            continue;
          }

          throw error;
        }
      }

      throw lastError || new Error('No fue posible descargar el archivo ZIP.');
    },
    formatBytes(bytes) {
      const size = Number(bytes || 0);
      if (size <= 0) {
        return '0 B';
      }

      const units = ['B', 'KB', 'MB', 'GB'];
      const pow = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
      const value = size / Math.pow(1024, pow);
      return `${value.toFixed(pow === 0 ? 0 : 2)} ${units[pow]}`;
    },
    isDownloadingBundle(requestId) {
      return Boolean(this.bundleActionState.downloading[requestId]);
    },
    isDeletingBundle(requestId) {
      return Boolean(this.bundleActionState.deleting[requestId]);
    },
    isCancelingManualDownload(groupId) {
      return Boolean(this.bundleActionState.cancelingManual[groupId]);
    },
    manualDownloadActionLabel(item) {
      const status = String(item?.status || '').trim().toLowerCase();
      return status === 'failed' ? 'Borrar' : 'Cancelar';
    },
    canCancelManualDownload(item) {
      const status = String(item?.status || '').trim().toLowerCase();
      return ['requested', 'in_progress', 'failed'].includes(status);
    },
    async fetchHistory(options = {}) {
      const showPendingModal = Boolean(options?.showPendingModal);

      if (!this.ensureRfcSelected()) {
        return;
      }

      this.loading = true;
      this.error = '';

      try {
        const response = await ws.getDownloadCenterHistory(this.rfc, 1, 200, { skipLoading: true });

        if (response && response.type === 'success') {
          this.groups = {
            manual_today: response?.groups?.manual_today || [],
            daily_last_10_days: response?.groups?.daily_last_10_days || [],
            monthly_last_12_months: response?.groups?.monthly_last_12_months || [],
            initial_summary: response?.groups?.initial_summary || null,
          };
          this.cfdiFileDownloads = response?.cfdi_file_downloads || [];
          this.manualLimits = response?.manual_limits || this.manualLimits;
          this.efirmaIncident = response?.efirma_incident || null;

          if (showPendingModal) {
            const pendingHint = response?.pending_request_hint || this.buildPendingHintFromGroups();
            this.applyPendingRequestHint(pendingHint || null);
          }

          return;
        }

        this.efirmaIncident = null;
        this.error = 'No fue posible consultar el historial del Centro de Descargas.';
      } catch (error) {
        this.efirmaIncident = null;
        this.error = error?.response?.data?.message || 'No fue posible consultar el historial del Centro de Descargas.';
      } finally {
        this.loading = false;
      }
    },
    openManualDownloadModal() {
      if (!this.ensureRfcSelected()) {
        return;
      }

      if (!this.canRequestDownloads) {
        return;
      }

      this.manualDownloadModal.period = getGlobalPeriod();
      this.manualDownloadModal.visible = true;
      this.manualDownloadModal.loading = false;
    },
    closeManualDownloadModal() {
      this.manualDownloadModal.visible = false;
      this.manualDownloadModal.period = '';
      this.manualDownloadModal.loading = false;
    },
    changeManualDownloadPeriod(period) {
      this.manualDownloadModal.period = String(period || '').trim();
    },
    openConfirmActionModal(payload = {}) {
      this.confirmActionModal.visible = true;
      this.confirmActionModal.loading = false;
      this.confirmActionModal.title = String(payload?.title || 'Confirmar acción');
      this.confirmActionModal.message = String(payload?.message || '¿Deseas continuar con esta acción?');
      this.confirmActionModal.confirmLabel = String(payload?.confirmLabel || 'Confirmar');
      this.confirmActionModal.loadingLabel = String(payload?.loadingLabel || 'Procesando...');
      this.confirmActionModal.action = typeof payload?.action === 'function' ? payload.action : null;
    },
    closeConfirmActionModal() {
      this.confirmActionModal.visible = false;
      this.confirmActionModal.loading = false;
      this.confirmActionModal.title = '';
      this.confirmActionModal.message = '';
      this.confirmActionModal.confirmLabel = '';
      this.confirmActionModal.loadingLabel = '';
      this.confirmActionModal.action = null;
    },
    openAlertModal(message, title = 'Aviso') {
      this.alertModal.visible = true;
      this.alertModal.title = String(title || 'Aviso');
      this.alertModal.message = String(message || 'Ocurrió un problema al procesar tu solicitud.');
    },
    closeAlertModal() {
      this.alertModal.visible = false;
      this.alertModal.title = '';
      this.alertModal.message = '';
    },
    async runConfirmAction() {
      if (this.confirmActionModal.loading || typeof this.confirmActionModal.action !== 'function') {
        return;
      }

      this.confirmActionModal.loading = true;

      try {
        await this.confirmActionModal.action();
        this.closeConfirmActionModal();
      } catch (error) {
        this.confirmActionModal.loading = false;
      }
    },
    async submitManualDownload() {
      if (!this.ensureRfcSelected()) {
        return;
      }

      const period = String(this.manualDownloadModal.period || '').trim();
      if (!period || this.manualDownloadModal.loading) {
        return;
      }

      this.manualDownloadModal.loading = true;

      try {
        const response = await ws.scheduleManualDownload(this.rfc, period, { skipLoading: true });

        if (response && response.type === 'success') {
          this.manualDownloadModal.visible = false;
          this.manualDownloadModal.period = '';
          await this.fetchHistory({ showPendingModal: true });
          return;
        }

        this.openAlertModal(response?.message || 'No fue posible programar la descarga manual.');
      } catch (error) {
        this.openAlertModal(error?.response?.data?.message || 'No fue posible programar la descarga manual.');
      } finally {
        this.manualDownloadModal.loading = false;
      }
    },
    async cancelManualDownload(item) {
      const groupId = String(item?.group_id || '').trim();
      const requestIds = Array.isArray(item?.request_ids) ? item.request_ids : [];
      if (!groupId || this.isCancelingManualDownload(groupId)) {
        return;
      }

      const actionLabel = this.manualDownloadActionLabel(item);

      this.openConfirmActionModal({
        title: `${actionLabel} descarga manual`,
        message: `¿Deseas ${actionLabel.toLowerCase()} esta descarga manual?`,
        confirmLabel: actionLabel,
        loadingLabel: actionLabel === 'Borrar' ? 'Borrando...' : 'Cancelando...',
        action: () => this.executeCancelManualDownload(item),
      });
    },

    async executeCancelManualDownload(item) {
      const groupId = String(item?.group_id || '').trim();
      const requestIds = Array.isArray(item?.request_ids) ? item.request_ids : [];
      if (!groupId || this.isCancelingManualDownload(groupId)) {
        return;
      }

      const actionLabel = this.manualDownloadActionLabel(item);

      this.bundleActionState.cancelingManual = {
        ...this.bundleActionState.cancelingManual,
        [groupId]: true,
      };

      try {
        const response = await ws.cancelManualDownload(this.rfc, groupId, requestIds, { skipLoading: true });
        if (response?.type === 'success') {
          await this.fetchHistory();
          return;
        }

        this.openAlertModal(response?.message || `No fue posible ${actionLabel.toLowerCase()} la descarga manual.`);
      } catch (error) {
        this.openAlertModal(error?.response?.data?.message || `No fue posible ${actionLabel.toLowerCase()} la descarga manual.`);
      } finally {
        this.bundleActionState.cancelingManual = {
          ...this.bundleActionState.cancelingManual,
          [groupId]: false,
        };
      }
    },
    async downloadCfdiFilesBundle(item) {
      const requestId = Number(item?.id || 0);
      if (!requestId || this.isDownloadingBundle(requestId)) {
        return;
      }

      this.bundleActionState.downloading = {
        ...this.bundleActionState.downloading,
        [requestId]: true,
      };

      try {
        const blob = await this.requestBundleDownloadWithRetry(requestId, 1);

        const fileName = item?.zip_name || `cfdis-${requestId}.zip`;
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        await this.fetchHistory();
      } catch (error) {
        const errorMessage = await this.resolveBundleDownloadErrorMessage(error);
        this.openAlertModal(errorMessage);
      } finally {
        this.bundleActionState.downloading = {
          ...this.bundleActionState.downloading,
          [requestId]: false,
        };
      }
    },
    async deleteCfdiFilesBundle(item) {
      const requestId = Number(item?.id || 0);
      if (!requestId || this.isDeletingBundle(requestId)) {
        return;
      }

      this.openConfirmActionModal({
        title: 'Borrar descarga de CFDIs',
        message: '¿Deseas eliminar esta solicitud y su archivo ZIP?',
        confirmLabel: 'Borrar',
        loadingLabel: 'Borrando...',
        action: () => this.executeDeleteCfdiFilesBundle(item),
      });
    },

    async executeDeleteCfdiFilesBundle(item) {
      const requestId = Number(item?.id || 0);
      if (!requestId || this.isDeletingBundle(requestId)) {
        return;
      }

      this.bundleActionState.deleting = {
        ...this.bundleActionState.deleting,
        [requestId]: true,
      };

      try {
        const response = await ws.deleteCfdiFilesBundle(this.rfc, requestId, { skipLoading: true });
        if (response?.type === 'success') {
          await this.fetchHistory();
          return;
        }

        this.openAlertModal(response?.message || 'No fue posible eliminar la solicitud.');
      } catch (error) {
        this.openAlertModal(error?.response?.data?.message || 'No fue posible eliminar la solicitud.');
      } finally {
        this.bundleActionState.deleting = {
          ...this.bundleActionState.deleting,
          [requestId]: false,
        };
      }
    },
  },
};
</script>

<style scoped>
.download-center-card {
  border: 1px solid var(--sidebar-border);
}

.download-center-limits {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 12px;
  color: var(--theme-font-color);
}

.download-center-table {
  font-size: 12px;
}

.download-group {
  border: 1px solid var(--sidebar-border);
  border-radius: 8px;
  background: var(--white);
  overflow: hidden;
}

.download-group-header {
  padding: 10px 12px;
  border-bottom: 1px solid var(--sidebar-border);
  background: rgba(82, 82, 108, 0.06);
}

.download-group-manual {
  border-color: rgba(115, 102, 255, 0.35);
}

.download-group-manual .download-group-header {
  background: linear-gradient(100deg, rgba(115, 102, 255, 0.11), rgba(47, 138, 182, 0.1));
}

.stage-badge,
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}

.stage-initial {
  background: rgba(47, 138, 182, 0.15);
  color: #2f8ab6;
}

.stage-daily {
  background: rgba(199, 137, 31, 0.15);
  color: #a06912;
}

.stage-manual {
  background: rgba(115, 102, 255, 0.15);
  color: rgb(115, 102, 255);
}

.status-requested,
.status-in_progress {
  background: rgba(199, 137, 31, 0.15);
  color: #a06912;
}

.status-downloading {
  background: rgba(47, 138, 182, 0.15);
  color: #2f8ab6;
}

.status-downloaded {
  background: rgba(37, 168, 108, 0.15);
  color: #1e8c59;
}

.status-completed {
  background: rgba(37, 168, 108, 0.15);
  color: #1e8c59;
}

.status-pending,
.status-requested {
  background: rgba(199, 137, 31, 0.15);
  color: #a06912;
}

.status-partial,
.status-processing,
.status-in_progress {
  background: rgba(115, 102, 255, 0.15);
  color: rgb(115, 102, 255);
}

.status-available {
  background: rgba(47, 138, 182, 0.15);
  color: #2f8ab6;
}

.status-failed {
  background: rgba(247, 49, 100, 0.15);
  color: rgb(247, 49, 100);
}

.manual-download-period {
  margin-top: 14px;
}

.pending-request-modal-dialog {
  max-width: 560px;
}

.pending-request-modal-content {
  border-radius: 14px;
  overflow: hidden;
}

.pending-request-modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 16px 20px;
}

.pending-request-modal-body {
  padding: 18px 20px 14px;
}

.pending-request-title {
  font-size: 20px;
  font-weight: 700;
  color: #2f2f4a;
}

.pending-request-subtitle {
  font-size: 14px;
  color: #6c757d;
}

.pending-request-info-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  background: #f4f6fb;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
}

.pending-request-info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pending-request-info-label {
  font-size: 11px;
  font-weight: 700;
  color: #8a8da2;
  text-transform: uppercase;
  letter-spacing: .4px;
}

.pending-request-info-value {
  font-size: 15px;
  font-weight: 700;
  color: #2f3b59;
}

.pending-request-warning {
  display: flex;
  gap: 10px;
  border: 1px solid #ffd266;
  background: #fff8e5;
  border-radius: 10px;
  padding: 12px 14px;
  color: #7a5a00;
}

.pending-request-warning i {
  font-size: 18px;
  margin-top: 2px;
}

.pending-request-warning strong {
  display: block;
  font-size: 14px;
  margin-bottom: 2px;
}

.pending-request-warning p {
  margin: 0;
  font-size: 13px;
}

.pending-request-modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 12px 20px 16px;
}

@media (max-width: 576px) {
  .pending-request-info-row {
    grid-template-columns: 1fr;
  }
}

.manual-download-period :deep(.ym-select) {
  max-width: 100%;
  width: 100%;
}
</style>
